import { ELEMENT_LINK, KEY_DESERIALIZE_HTML } from '@udecode/plate';
import { Editor, Node, Range, Transforms } from 'slate';
import { HistoryEditor } from 'slate-history';

import createUndoHistoryBreak from 'components/text_editor_new/lib/create_undo_history_break';
import linkifyIt, { isUrlValid } from 'scripts/presentation/components/lib/linkify_it';

// Chat-only autolink: wraps a bare URL in an ELEMENT_LINK when the agent types
// a space immediately after it, or when they paste text containing URLs —
// plain text via the insertData override below, HTML via a transformFragment
// hook injected into Plate's deserializer. Other rich-text composers (email,
// notes, tasks) intentionally do not load this plugin — their downstream
// rendering pipelines do not rely on anchors and existing specs assert raw
// URL output.
export function createAutolinkPlugin() {
  return {
    key: 'autolink',
    inject: {
      pluginsByKey: {
        [KEY_DESERIALIZE_HTML]: {
          editor: {
            insertData: {
              transformFragment: linkifyFragment,
            },
          },
        },
      },
    },
    withOverrides: editor => {
      const { insertData: unoverriddenInsertData, insertText: unoverriddenInsertText } = editor;

      editor.insertText = text => {
        if (text === ' ' && editor.selection && Range.isCollapsed(editor.selection)) {
          // Mirror the capitalize_i / capitalize_first_word pattern: insert the
          // user's keystroke in its own history segment, push an undo break,
          // then apply the autolink. Cmd-Z then undoes only the autolink and
          // preserves the typed space.
          HistoryEditor.withoutMerging(editor, () => {
            unoverriddenInsertText(text);
          });
          createUndoHistoryBreak(editor);
          autolinkBeforeSpace(editor);
          return;
        }
        unoverriddenInsertText(text);
      };

      editor.insertData = data => {
        if (insertPastedUrl(editor, data)) return;
        unoverriddenInsertData(data);
      };

      return editor;
    },
  };
}

function autolinkBeforeSpace(editor) {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) return;
  if (Editor.above(editor, { match: n => n.type === ELEMENT_LINK })) return;

  const blockEntry = Editor.above(editor, { match: n => Editor.isBlock(editor, n) });
  if (!blockEntry) return;
  const [, blockPath] = blockEntry;

  const blockStart = Editor.start(editor, blockPath);
  const cursor = selection.focus;
  const blockText = Editor.string(editor, { anchor: blockStart, focus: cursor });
  const matches = linkifyIt.match(blockText);
  if (!matches?.length) return;

  // The trailing character is the space we just inserted; the URL must end
  // exactly one character before the cursor.
  const lastMatch = matches[matches.length - 1];
  if (lastMatch.lastIndex !== blockText.length - 1) return;
  if (!isUrlValid(lastMatch.url)) return;

  const matchStart = pointAtBlockOffset(editor, blockPath, lastMatch.index);
  const matchEnd = pointAtBlockOffset(editor, blockPath, lastMatch.lastIndex);
  if (!matchStart || !matchEnd) return;

  // If the URL match overlaps an existing link node in the block (for example
  // because the agent just pasted a URL that the paste path already wrapped),
  // skip — wrapping a range that straddles an existing link would nest it
  // inside an outer link covering the surrounding text. Editor.above on a
  // straddling range resolves to the block's common ancestor and misses the
  // inner link, so use an explicit character-range overlap check.
  if (blockHasLinkOverlapping(editor, blockPath, lastMatch.index, lastMatch.lastIndex)) return;

  const matchRange = { anchor: matchStart, focus: matchEnd };

  // Wrap the URL leaves rather than delete+re-insert so the surrounding text
  // (including the just-typed trailing space) stays put and any active marks
  // on the URL leaves (bold/italic/underline/color/fontSize) are preserved.
  Editor.withoutNormalizing(editor, () => {
    Transforms.wrapNodes(
      editor,
      { type: ELEMENT_LINK, url: lastMatch.url, target: '_blank' },
      { at: matchRange, split: true }
    );
  });

  // Restore the cursor to the end of the block — after the typed space.
  const blockEnd = Editor.end(editor, blockPath);
  Transforms.setSelection(editor, { anchor: blockEnd, focus: blockEnd });
}

function insertPastedUrl(editor, data) {
  if (!editor.selection) return false;
  if (data.getData('text/html')) return false;

  const text = data.getData('text/plain');
  if (!text) return false;

  const trimmed = text.trim();
  if (!trimmed) return false;

  if (Editor.above(editor, { match: n => n.type === ELEMENT_LINK })) return false;

  // Capture active marks before any selection-replace `delete` clears them.
  const marks = Editor.marks(editor) || {};

  // The whole paste is a single bare URL: drop the surrounding whitespace and
  // insert just the link.
  const trimmedMatches = linkifyIt.match(trimmed);
  if (trimmedMatches?.length === 1 && trimmedMatches[0].text === trimmed) {
    if (!isUrlValid(trimmedMatches[0].url)) return false;
    Editor.withoutNormalizing(editor, () => {
      if (!Range.isCollapsed(editor.selection)) {
        Transforms.delete(editor);
      }
      Transforms.insertNodes(editor, {
        type: ELEMENT_LINK,
        url: trimmedMatches[0].url,
        target: '_blank',
        children: [{ text: trimmed, ...marks }],
      });
    });
    return true;
  }

  // Mixed content: insert the pasted text verbatim with each URL wrapped in a
  // link. Lines are split the same way slate-react's default plain-text
  // insertData splits them, so multiline pastes behave as before.
  const lineSegments = text.split(/\r\n|\r|\n/).map(segmentLineByUrls);
  if (!lineSegments.some(segments => segments.some(segment => segment.url))) return false;

  Editor.withoutNormalizing(editor, () => {
    if (!Range.isCollapsed(editor.selection)) {
      Transforms.delete(editor);
    }
    lineSegments.forEach((segments, lineIndex) => {
      if (lineIndex > 0) Transforms.splitNodes(editor, { always: true });
      const nodes = segmentsToInlineNodes(segments, marks);
      if (nodes.length) Transforms.insertFragment(editor, nodes);
    });
  });
  return true;
}

// Builds the text/link nodes for one pasted line. Every link is padded with
// adjacent text nodes so insertFragment leaves the cursor outside the link —
// inserting a bare link node would put the cursor inside it and route the
// rest of the paste (or subsequent typing) into the link.
function segmentsToInlineNodes(segments, marks) {
  const nodes = [];
  for (const segment of segments) {
    if (segment.url) {
      if (!nodes.length || nodes[nodes.length - 1].type === ELEMENT_LINK) {
        nodes.push({ text: '', ...marks });
      }
      nodes.push({
        type: ELEMENT_LINK,
        url: segment.url,
        target: '_blank',
        children: [{ text: segment.text, ...marks }],
      });
    } else if (segment.text) {
      nodes.push({ text: segment.text, ...marks });
    }
  }
  if (nodes.length && nodes[nodes.length - 1].type === ELEMENT_LINK) {
    nodes.push({ text: '', ...marks });
  }
  return nodes;
}

// transformFragment hook for HTML pastes: after Plate's deserializer builds
// the fragment, wrap bare URLs sitting in text leaves. Anchor tags have
// already become ELEMENT_LINK nodes by this point, so link subtrees are
// skipped to avoid double-wrapping.
function linkifyFragment(fragment) {
  return fragment.flatMap(linkifyNode);
}

function linkifyNode(node) {
  if (node.type === ELEMENT_LINK) return [node];
  if (node.children) return [{ ...node, children: node.children.flatMap(linkifyNode) }];
  if (!node.text) return [node];

  const { text, ...marks } = node;
  const segments = segmentLineByUrls(text);
  if (!segments.some(segment => segment.url)) return [node];
  return segmentsToInlineNodes(segments, marks);
}

function segmentLineByUrls(line) {
  const matches = (linkifyIt.match(line) || []).filter(match => isUrlValid(match.url));
  if (!matches.length) return [{ text: line }];

  const segments = [];
  let offset = 0;
  for (const match of matches) {
    if (match.index > offset) segments.push({ text: line.slice(offset, match.index) });
    segments.push({ text: match.text, url: match.url });
    offset = match.lastIndex;
  }
  if (offset < line.length) segments.push({ text: line.slice(offset) });
  return segments;
}

function pointAtBlockOffset(editor, blockPath, offset) {
  const range = { anchor: Editor.start(editor, blockPath), focus: Editor.end(editor, blockPath) };
  let i = 0;
  for (const point of Editor.positions(editor, { at: range, unit: 'character' })) {
    if (i === offset) return point;
    i++;
  }
  return null;
}

function blockHasLinkOverlapping(editor, blockPath, startOffset, endOffset) {
  const block = Node.get(editor, blockPath);
  let offset = 0;
  for (const child of block.children) {
    const length = Node.string(child).length;
    if (child.type === ELEMENT_LINK) {
      const childStart = offset;
      const childEnd = offset + length;
      // Touching at a boundary (e.g. matchEnd === childStart) is not an overlap.
      if (startOffset < childEnd && endOffset > childStart) return true;
    }
    offset += length;
  }
  return false;
}
